import React from 'react';
import { Box, Typography } from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import RouteIcon from '@mui/icons-material/Route';
import SpeedIcon from '@mui/icons-material/Speed';
import GroupsIcon from '@mui/icons-material/Groups';
import polarrideLogo from '@/assets/polarride-logo.png';

const GOLD = '#E2B84B';

const GlassCard: React.FC<{
  icon: React.ReactNode;
  label: string;
}> = ({ icon, label }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      px: 2.5,
      py: 1.5,
      borderRadius: 2.5,
      backgroundColor: 'rgba(226,184,75,0.1)',
      border: '1px solid rgba(226,184,75,0.25)',
      transition: 'all 0.3s ease',
      '&:hover': {
        backgroundColor: 'rgba(226,184,75,0.18)',
        border: '1px solid rgba(226,184,75,0.45)',
        transform: 'translateY(-2px)',
      },
    }}
  >
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: 2,
        backgroundColor: 'rgba(226,184,75,0.2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Typography
      variant="body2"
      sx={{ fontWeight: 600, fontSize: '0.82rem', color: '#ffffff !important' }}
    >
      {label}
    </Typography>
  </Box>
);

export const AuthIllustration: React.FC = () => {
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#000000',
        position: 'relative',
        overflow: 'hidden',
        p: 6,
      }}
    >
      {/* Gold ambient glow — top right */}
      <Box
        sx={{
          position: 'absolute',
          top: '-10%',
          right: '-5%',
          width: 500,
          height: 500,
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(226,184,75,0.35) 0%, transparent 65%)',
          filter: 'blur(60px)',
          pointerEvents: 'none',
        }}
      />
      {/* Gold ambient glow — bottom left */}
      <Box
        sx={{
          position: 'absolute',
          bottom: '-10%',
          left: '-5%',
          width: 450,
          height: 450,
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(226,184,75,0.25) 0%, transparent 65%)',
          filter: 'blur(60px)',
          pointerEvents: 'none',
        }}
      />
      {/* Gold ambient glow — center subtle */}
      <Box
        sx={{
          position: 'absolute',
          top: '40%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 600,
          height: 600,
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(226,184,75,0.08) 0%, transparent 60%)',
          filter: 'blur(80px)',
          pointerEvents: 'none',
        }}
      />

      {/* Subtle grid pattern */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: 0.06,
          backgroundImage:
            'linear-gradient(rgba(226,184,75,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(226,184,75,0.5) 1px, transparent 1px)',
          backgroundSize: '56px 56px',
          pointerEvents: 'none',
        }}
      />

      {/* Content */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          maxWidth: 480,
        }}
      >
        {/* Logo mark */}
        <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Box
          component="img"
          src={polarrideLogo}
          alt="PolarRide"
          sx={{
            height: 60,
            width: 'auto',
          }}
        />
      </Box>

        {/* Brand name */}
        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            color: '#ffffff !important',
            mb: 0.5,
            letterSpacing: '-0.03em',
            fontSize: { md: '2.2rem', lg: '2.8rem' },
          }}
        >
          Polar
          <Box component="span" sx={{ color: `${GOLD} !important` }}>
            Ride
          </Box>
        </Typography>

        {/* Subtitle */}
        <Typography
          sx={{
            color: '#ffffff !important',
            fontWeight: 600,
            mb: 4,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            fontSize: '0.75rem',
          }}
        >
          Admin Dashboard
        </Typography>

        {/* Gold divider */}
        <Box
          sx={{
            width: 60,
            height: 2,
            borderRadius: 1,
            background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`,
            mb: 4,
          }}
        />

        {/* Description */}
        <Typography
          variant="body1"
          sx={{
            color: '#ffffff !important',
            textAlign: 'center',
            maxWidth: 340,
            lineHeight: 1.7,
            mb: 5,
            fontSize: '1rem',
          }}
        >
          Manage your fleet, drivers, and bookings — all from one powerful
          dashboard.
        </Typography>

        {/* Feature cards */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 2,
            width: '100%',
            maxWidth: 500,
          }}
        >
          <GlassCard
            icon={<DirectionsCarIcon sx={{ fontSize: 18, color: GOLD }} />}
            label="Fleet Management"
          />
          <GlassCard
            icon={<RouteIcon sx={{ fontSize: 18, color: GOLD }} />}
            label="Live Tracking"
          />
          <GlassCard
            icon={<GroupsIcon sx={{ fontSize: 18, color: GOLD }} />}
            label="Driver Oversight"
          />
          <GlassCard
            icon={<SpeedIcon sx={{ fontSize: 18, color: GOLD }} />}
            label="Real-time Analytics"
          />
        </Box>
      </Box>

      {/* Bottom gold accent line */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 2,
          background: `linear-gradient(90deg, transparent 10%, ${GOLD} 50%, transparent 90%)`,
          opacity: 0.5,
        }}
      />
    </Box>
  );
};
